/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';

import { useProductListContext } from '@utils/productList';

import ChevronDuoLeftSvg from './icons/chevron_duo_left.svg';
import ChevronDuoRightSvg from './icons/chevron_duo_right.svg';
import ChevronLeftSvg from './icons/chevron_left.svg';
import ChevronRightSvg from './icons/chevron_right.svg';

import type { Theme } from '@emotion/react';
import type { Interpolation } from '@emotion/serialize';

type PaginationButtonType = {
  ariaLabel: string;
  children: React.ReactNode;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  cssProps?: Interpolation<Theme>;
  selected?: boolean;
};

const PaginationButton: React.FC<PaginationButtonType> = ({
  ariaLabel,
  children,
  cssProps,
  onClick,
  selected = false
}) => {
  const activeButtonFn = (t) => ({
    color: 'white',
    backgroundColor: t.color.secondary
  });

  return (
    <button
      aria-label={ariaLabel}
      type="button"
      onClick={onClick}
      css={(t) => ({
        border: `thin solid ${t.color.secondary}`,
        borderRadius: 3,
        color: 'black',
        cursor: 'pointer',
        fontSize: t.font.size.small,
        lineHeight: '38px',
        maxHeight: '40px',
        width: 40,
        ':hover': activeButtonFn(t),
        ...(selected && activeButtonFn(t)),
        ...(cssProps as object)
      })}
    >
      {children}
    </button>
  );
};

const PaginationBar: React.FC<{ cssProps?: Interpolation<Theme> }> = ({ cssProps = '' }) => {
  const t = useTheme();
  const {
    activeFiltersAndSorting: { currentPage },
    defaultSettings: { productsPerPage },
    productsAndAvailableFilters: { total },
    reducerFunctions: { setCurrentPage }
  } = useProductListContext();
  const { scrollContainerRefIntoView } = useProductListContext();
  const totalPages = Math.ceil(total / productsPerPage);
  const maxPages = 5;
  const shownPages = Math.min(maxPages, totalPages);
  const firstPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const setCurrentPageAndScroll = (page: number) => {
    setCurrentPage(page);
    if (scrollContainerRefIntoView) {
      scrollContainerRefIntoView();
    }
  };
  const buttonAdditionalCss = {
    alignItems: 'center',
    border: 'none',
    display: 'flex',
    height: '40px',
    justifyContent: 'center',
    margin: '0 0.125rem',
    svg: {
      path: {
        fill: t.color.secondary
      }
    },

    ':hover': {
      backgroundColor: 'white',
      'svg path': {
        fill: t.color.primary
      }
    }
  };

  return (
    <div css={() => ({ display: 'flex', justifyContent: 'center', width: '100%', ...(cssProps as object) })}>
      <div style={{ display: 'flex', visibility: currentPage === 1 ? 'hidden' : 'visible' }}>
        <PaginationButton
          ariaLabel="Pagination - go to first page"
          cssProps={buttonAdditionalCss}
          onClick={() => setCurrentPageAndScroll(1)}
        >
          <ChevronDuoLeftSvg />
        </PaginationButton>
        <PaginationButton
          ariaLabel={`Pagination - go to previous page (${currentPage - 1})`}
          cssProps={buttonAdditionalCss}
          onClick={() => setCurrentPageAndScroll(currentPage - 1)}
        >
          <ChevronLeftSvg />
        </PaginationButton>
      </div>

      {[...Array(shownPages)].map((_, i) => (
        <PaginationButton
          ariaLabel={`Pagination - go to page ${i + firstPage}`}
          cssProps={{ margin: '0 0.125rem' }}
          onClick={() => setCurrentPageAndScroll(i + firstPage)}
          selected={i + firstPage === currentPage}
          key={i + firstPage}
        >
          {i + firstPage}
        </PaginationButton>
      ))}

      <div style={{ display: 'flex', visibility: currentPage === totalPages ? 'hidden' : 'visible' }}>
        <PaginationButton
          ariaLabel={`Pagination - go to next page (${currentPage + 1})`}
          cssProps={buttonAdditionalCss}
          onClick={() => setCurrentPageAndScroll(currentPage + 1)}
        >
          <ChevronRightSvg />
        </PaginationButton>
        <PaginationButton
          ariaLabel="Pagination - go to last page"
          cssProps={buttonAdditionalCss}
          onClick={() => setCurrentPageAndScroll(totalPages)}
        >
          <ChevronDuoRightSvg />
        </PaginationButton>
      </div>
    </div>
  );
};

export default PaginationBar;
export { PaginationButton };
